import { useEffect, useState } from 'react';
import type { FactoryMetrics } from '@reef/schema';
import {
  api,
  type ConnectionSummary,
  type FactorySummary,
  type InboxResponse,
  type RunRow,
} from '../api.ts';
import type { DeploymentReadiness } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { InboxList, relativeTime } from '../components/InboxList.tsx';
import { StageStrip } from '../components/StageStrip.tsx';
import type { Route } from '../routes.ts';

/**
 * The front door: what needs you, what the factories are doing, and — for a
 * deployment that has not done anything yet — what to do first.
 *
 * Three questions, in the order a person asks them. "Is anything waiting on
 * me" comes first because it is the only one with a deadline; the factories
 * come next because they are what this product is; the numbers come last
 * because a number is only interesting once you know what it is a number of.
 *
 * The get-started card is not a tour. It is four facts about this deployment
 * that each block something — no GitHub means no pull request, no Linear
 * means no ticket arrives — and it goes away on its own when they are true.
 */
export function HomeView({
  factories,
  connections,
  inbox,
  isAdmin,
  onDecided,
  stopped,
  go,
}: {
  factories: FactorySummary[];
  connections: ConnectionSummary[];
  /**
   * The shell's copy of what is waiting, not a fetch of this screen's own.
   * The badge, this panel and the Inbox then show one list — and a decision
   * made here is a decision the badge learns of from the same refresh.
   */
  inbox: InboxResponse | null;
  isAdmin: boolean;
  onDecided(): void;
  stopped: boolean;
  go(route: Partial<Route> & { tab: Route['tab'] }): void;
}) {
  const [metrics, setMetrics] = useState<FactoryMetrics | null>(null);
  const [inFlight, setInFlight] = useState<RunRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    api
      .factoryMetrics(24)
      .then((result) => current && setMetrics(result.metrics))
      .catch((caught) => current && setError((caught as Error).message));
    /*
     * The same read the Runs screen makes under its "In flight" pill, so the
     * two agree: a run listed here is one that screen lists, and its "All
     * runs" button lands on the same rows. Reading `/goals` instead gave Home
     * a second definition of in-flight to drift from the first.
     */
    api
      .listRecentRuns({ status: 'in-flight' })
      .then((result) => current && setInFlight(result.runs))
      .catch(() => {});
    return () => {
      current = false;
    };
  }, []);

  // The same split the topbar pill uses: only the links *you* can make. A
  // missing bot credential is an admin's to set, and counting it here put a
  // number under "Needs you" that a member could not bring down.
  const needsLink = connections.filter(
    (connection) => connection.usesUser && !connection.userLinked && connection.connectUrl !== null,
  );
  const github = connections.find((connection) => connection.id === 'github-app');
  const linear = connections.find((connection) => connection.id === 'linear');
  const deployed = factories.reduce((total, factory) => total + factory.deployed, 0);
  const stagesTotal = factories.reduce((total, factory) => total + factory.stages.length, 0);
  const everRun = inFlight.length > 0 || (metrics?.throughput ?? 0) > 0 || (metrics?.backlog ?? 0) > 0;

  /*
   * The items themselves, not a count of them. "2 plans waiting → Open" sent a
   * person to find the plan, decide, and come back; the plan is a sentence and
   * the decision is one button, so both are here. Five, newest first — the
   * ones that just arrived are the ones nobody knows about yet — and the rest
   * are one click away in the Inbox.
   */
  const waiting = (inbox?.count ?? 0) + needsLink.length;
  const SHOWN = 5;
  const more = Math.max(0, (inbox?.count ?? 0) - SHOWN);

  /*
   * Fetched here rather than handed down: the two items it adds are this
   * card's alone, and a failed read leaves the card as it was rather than
   * blocking Home — a deployment that cannot say what it is missing has still
   * got a front door.
   */
  const [readiness, setReadiness] = useState<DeploymentReadiness | null>(null);
  useEffect(() => {
    let current = true;
    api
      .deploymentReadiness()
      .then((result) => current && setReadiness(result))
      .catch(() => {});
    return () => {
      current = false;
    };
  }, []);

  /*
   * The two facts that block everything below them, first. Absent until
   * readiness has answered rather than shown as undone, because a card that
   * flashed "Set a model provider" on every load and then withdrew it would
   * teach people to stop reading the card.
   */
  const deployment = readiness
    ? [
        ...(readiness.modelProvider.ready
          ? []
          : [
              {
                done: false,
                title: 'Set a model provider',
                body: `Every step runs on one. Until the ${readiness.modelProvider.provider} key is set, a run starts and fails at dispatch.`,
                action: () => go({ tab: 'settings', section: 'admin' }),
                label: 'See Admin',
              },
            ]),
        ...(readiness.configRepo.ready
          ? []
          : [
              {
                done: false,
                title: 'Connect the config repository',
                body: 'Editing a policy is a commit; until this is set, policies are read-only. Workflows and factories are unaffected.',
                action: () => go({ tab: 'settings', section: 'admin' }),
                label: 'See Admin',
              },
            ]),
      ]
    : [];

  const steps = [
    ...deployment,
    {
      done: Boolean(github?.authorized || github?.userLinked),
      title: 'Connect GitHub',
      body: 'Every stage that reads a repository or opens a pull request goes through it.',
      action: () => go({ tab: 'connections', connectionId: 'github-app' }),
      label: 'Connect',
    },
    {
      done: Boolean(linear?.authorized || linear?.userLinked),
      title: 'Connect Linear',
      body: 'The fastest way in: an issue moving into Triage starts the lifecycle on its own.',
      action: () => go({ tab: 'connections', connectionId: 'linear' }),
      label: 'Connect',
    },
    {
      done: stagesTotal > 0 && deployed === stagesTotal,
      title: 'Deploy the factory workflows',
      body:
        stagesTotal === 0
          ? 'No factory is defined.'
          : `${deployed} of ${stagesTotal} stages are deployed. Run pnpm seed:local to load the rest.`,
      action: () => go({ tab: 'factories' }),
      label: 'See factories',
    },
    {
      done: everRun,
      title: 'Start a piece of work',
      body: 'Describe a problem and watch it move through the stages.',
      action: () => go({ tab: 'work', runId: 'new' }),
      label: 'New work item',
    },
  ];
  const remaining = steps.filter((step) => !step.done).length;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Home</h1>
          <p className="page-lede">What the factory is doing, and what it needs from you.</p>
        </div>
        <button className="primary" onClick={() => go({ tab: 'work', runId: 'new' })}>
          <Icon name="plus" /> New work item
        </button>
      </header>

      {error && <div className="banner danger">{error}</div>}

      {stopped && (
        <div className="banner warn">
          <h3>The factory is stopped</h3>
          <p style={{ margin: 0 }}>
            Nothing will start until an operator releases the stop switch.{' '}
            <button className="linkish" onClick={() => go({ tab: 'settings', section: 'operations' })}>
              Open operations
            </button>
          </p>
        </div>
      )}

      <div className="page-grid">
        <section className="panel needs-you">
          <header className="panel-head">
            <h2>Needs you</h2>
            {waiting > 0 && <span className="chip warn">{waiting}</span>}
            <span className="spacer" />
            <button className="ghost" onClick={() => go({ tab: 'inbox' })}>
              Inbox <Icon name="arrow" />
            </button>
          </header>
          {inbox === null && needsLink.length === 0 ? (
            // Absent until the inbox has answered, rather than "nothing is
            // waiting" for a moment and then a list: a panel that says all
            // clear on every load and takes it back teaches people to stop
            // reading it.
            <p className="hint">Checking what is waiting…</p>
          ) : waiting === 0 ? (
            <p className="panel-quiet">
              <Icon name="check" /> Nothing is waiting on you.
            </p>
          ) : (
            <>
              {inbox && inbox.items.length > 0 && (
                <InboxList
                  items={inbox.items}
                  limit={SHOWN}
                  compact
                  isAdmin={isAdmin}
                  onDecided={onDecided}
                  go={go}
                />
              )}
              {more > 0 && (
                <p className="hint" style={{ margin: 0 }}>
                  <button className="linkish" onClick={() => go({ tab: 'inbox' })}>
                    {more} more in the Inbox
                  </button>
                </p>
              )}
              {needsLink.length > 0 && (
                <ul className="attention">
                  <li>
                    <span className="attention-count">{needsLink.length}</span>
                    <span>
                      {needsLink.length === 1 ? 'account to connect' : 'accounts to connect'}
                    </span>
                    <button className="ghost" onClick={() => go({ tab: 'connections' })}>
                      Open <Icon name="arrow" />
                    </button>
                  </li>
                </ul>
              )}
            </>
          )}
          {inbox && inbox.hidden > 0 && (
            /*
             * Said plainly rather than omitted, as the queues themselves do.
             * Somebody whose colleague sees three and who sees none needs to
             * know the difference is permission, not a bug.
             */
            <p className="hint" style={{ margin: 0 }}>
              {inbox.hidden} more {inbox.hidden === 1 ? 'is' : 'are'} waiting on workflows you have
              not been shared on.
            </p>
          )}
        </section>

        {remaining > 0 && (
          <section className="panel">
            <header className="panel-head">
              <h2>Get started</h2>
              <span className="chip">{steps.length - remaining} of {steps.length}</span>
            </header>
            <ol className="checklist">
              {steps.map((step) => (
                <li key={step.title} className={step.done ? 'done' : ''}>
                  <span className="checklist-mark" aria-hidden="true">
                    {step.done && <Icon name="check" />}
                  </span>
                  <div className="checklist-body">
                    <strong>{step.title}</strong>
                    <span className="hint">{step.body}</span>
                  </div>
                  {!step.done && (
                    <button className="ghost" onClick={step.action}>
                      {step.label} <Icon name="arrow" />
                    </button>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>

      <section className="panel">
        <header className="panel-head">
          <h2>Factories</h2>
          <span className="spacer" />
          <button className="ghost" onClick={() => go({ tab: 'factories' })}>
            All factories <Icon name="arrow" />
          </button>
        </header>
        <div className="factory-rows">
          {factories.map((factory) => (
            <button
              key={factory.id}
              type="button"
              className="factory-row"
              onClick={() => go({ tab: 'factories', factoryId: factory.id })}
            >
              <div className="factory-row-head">
                <strong>{factory.name}</strong>
                <span className={`chip ${factory.kind === 'delivery' ? 'accent' : ''}`}>{factory.kind}</span>
                <span className="hint">{factory.entry.description}</span>
                <span className="spacer" />
                {factory.deployed < factory.stages.length && (
                  <span className="chip warn">
                    {factory.deployed} of {factory.stages.length} deployed
                  </span>
                )}
              </div>
              <StageStrip stages={factory.stages} closesLoop={factory.closesLoop} compact />
            </button>
          ))}
          {factories.length === 0 && <p className="hint">No factories are defined in this deployment.</p>}
        </div>
      </section>

      <div className="page-grid">
        <section className="panel">
          <header className="panel-head">
            <h2>In flight</h2>
            <span className="spacer" />
            <button className="ghost" onClick={() => go({ tab: 'runs' })}>
              All runs <Icon name="arrow" />
            </button>
          </header>
          {inFlight.length === 0 ? (
            <p className="panel-quiet">Nothing is running right now.</p>
          ) : (
            <ul className="run-list">
              {inFlight.slice(0, 8).map((run) => (
                <li key={run.id}>
                  <button
                    type="button"
                    className="run-line"
                    onClick={() => go({ tab: 'runs', workflowId: run.workflowId, runId: run.id })}
                  >
                    <span className={`run-dot ${run.waiting ? 'warn' : 'accent'}`} />
                    <span className="run-line-name">{run.workflowName}</span>
                    <span className="hint">
                      {run.status.replace(/_/g, ' ')} · started {relativeTime(run.createdAt)}
                      {run.waiting && ' · waiting on a person'}
                    </span>
                    <span className="hint mono">{run.triggerType}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="panel">
          <header className="panel-head">
            <h2>Last 24 hours</h2>
            <span className="spacer" />
            <button className="ghost" onClick={() => go({ tab: 'insights', section: 'analytics' })}>
              Insights <Icon name="arrow" />
            </button>
          </header>
          <div className="tiles">
            <Tile label="Completed" value={metrics ? String(metrics.throughput) : '—'} note="runs finished" />
            <Tile label="In flight" value={metrics ? String(metrics.backlog) : '—'} note="runs still going" />
            <Tile
              label="Cycle time"
              value={metrics?.cycleTimeHours == null ? '—' : `${metrics.cycleTimeHours.toFixed(1)}h`}
              note="median, start to finish"
            />
            <Tile
              label="Failure rate"
              value={metrics ? `${Math.round(metrics.failureRate * 100)}%` : '—'}
              note="of runs that ended"
              tone={metrics && metrics.failureRate > 0.2 ? 'warn' : undefined}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

export function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone?: 'warn';
}) {
  return (
    <div className={`tile ${tone ?? ''}`}>
      <p className="tile-label">{label}</p>
      <p className="tile-value">{value}</p>
      <p className="tile-note">{note}</p>
    </div>
  );
}
